import React from 'react';
import { useNavigate } from 'react-router-dom';
import { linkPath } from 'common/constants';
import { menuItems } from './content';
import { MenuProps } from './types';
import { TabBarIcon, TabBarIconVariant } from './TabBarIcon';

// Maps the menuItems entries (defined in content.js for back-compat) to the
// new TabBarIcon variants. Indexed by item.dataCy so re-ordering content
// doesn't break the mapping.
const VARIANT_BY_DATA_CY: Record<string, TabBarIconVariant> = {
  'cta-home': 'home',
  'cta-sharing': 'shared',
  'cta-reminder': 'reminders',
  'cta-settings': 'search',
};

// The last tab used to route to /menu (settings) — Figma redesign moves it
// to the search-document page. Override the URL here so we don't have to
// touch the legacy content.js file.
const URL_OVERRIDES: Record<string, string> = {
  'cta-settings': linkPath.searchDocumentPath,
};

// Shepherd tour anchors for the variants it points at — indexed by variant
// (not data-cy) so it stays in step with TabBarIconVariant.
const DATA_TOUR_BY_VARIANT: Partial<Record<TabBarIconVariant, string>> = {
  reminders: 'nav-reminders',
  shared: 'nav-sharing',
};

export const Menu = ({ menuId, showGradient = true } : MenuProps) => {
  const navigate = useNavigate();
  const onClickHandler = (url: string) => {
    if (url) {
      navigate(url);
    }
  };
  return (
    <>
      <div className={showGradient ? 'doqit-menu-gradient' : ''} />
      <ul role="presentation" className="flex justify-between w-[100%] h-[100%]">
        {menuItems.map((item) => {
          const variant = VARIANT_BY_DATA_CY[item.dataCy];
          if (!variant) {
            // The middle "spacer" item (no icon) — preserve layout.
            return (
              <li
                key={item.id}
                className="col-span-1 flex flex-col items-center justify-center w-[100%]"
                style={{ background: '#F7F5F8' }}
                aria-hidden="true"
              />
            );
          }
          const url = URL_OVERRIDES[item.dataCy] || item.url || '';
          const isActive = item.id === menuId;
          return (
            <li
              key={item.id}
              className="col-span-1 flex flex-col items-center justify-center w-[100%] cursor-pointer"
              style={{ background: '#F7F5F8' }}
            >
              <button
                type="button"
                onClick={() => onClickHandler(url)}
                aria-label={item.title}
                aria-current={isActive ? 'page' : undefined}
                data-cy={`cta-${item.dataCy}`}
                data-tour={DATA_TOUR_BY_VARIANT[variant]}
                className="bg-transparent border-0 p-0 inline-flex items-center justify-center"
              >
                <TabBarIcon variant={variant} active={isActive} />
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default Menu;
